import React, { useEffect, useState } from "react";
import Layout from "../../../layouts/default";
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Form,
  Alert,
  Modal,
  Spinner,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCampStatesSummary, getSnsStatesSummary, getVenderResponseByCampaignUuid } from "../../../store/slices/campaigns/campaign";
import { getTodayCampaigns } from "../../../store/slices/campaigns/campaigns";
import { FileCode } from "react-bootstrap-icons";
import { DateTime } from "luxon";
import JsonModal from "./JsonModel";
import SmartPagination from "./DynamicSmartPagination";
import DataIssueSummary from "./EmailDataIssues/DataIssueSummary";

const CampaignRunReportByUuid = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { campaignUuid } = useParams();

  const { venderLogs } = useSelector((store) => store.campaignSlice);
  const venderLogsArr = Array.isArray(venderLogs?.payload) ? venderLogs.payload : [];
  const totalLogs = venderLogs?.totalCount || 0;

  const logsPerPage = 10;
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [data, setData] = useState({});

  const { snsStatSummary, camapignMoreInfo } = useSelector((store) => store.campaignSlice);

  const [loadingStates, setLoadingStates] = useState({
    campaign: true,
    sns: true,
    logs: true,
  });

  useEffect(() => {
    if (campaignUuid) {
      setLoadingStates((prev) => ({ ...prev, campaign: true, sns: true }));

      dispatch(getTodayCampaigns({}));
      dispatch(getSnsStatesSummary({ campaignUuid })).finally(() =>
        setLoadingStates((prev) => ({ ...prev, sns: false }))
      );
      dispatch(getCampStatesSummary({ campaignUuid })).finally(() =>
        setLoadingStates((prev) => ({ ...prev, campaign: false }))
      );
    }
  }, [dispatch, campaignUuid]);

  const fetchData = async (filters = {}) => {
    setError(null);
    setLoadingStates((prev) => ({ ...prev, logs: true }));

    try {
      const payload = {
        campaignUuid,
        page,
        limit: logsPerPage,
        ...filters,
      };

      const res = await dispatch(getVenderResponseByCampaignUuid(payload));
      const logData = res?.payload;
      const logsArray = Array.isArray(logData?.payload?.payload) ? logData.payload.payload : [];

      if (logsArray.length > 0) {
        setError(null);
      } else {
        setError("No vendor logs found.");
      }

      setData(logData?.payload || {});
    } catch (err) {
      console.error("Error fetching vendor logs:", err);
      setError("Failed to load vendor logs.");
    }

    setLoadingStates((prev) => ({ ...prev, logs: false }));
  };

  useEffect(() => {
    if (campaignUuid) {
      fetchData({ event: filterType, email: filterEmail });
    }
  }, [campaignUuid, page, logsPerPage]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/campaign/email-campaing-report");
    }
  };

  const handleFilter = () => {
    setPage(1);
    fetchData({
      event: filterType || undefined,
      email: filterEmail.trim() || undefined,
    });
  };

  const handleReset = () => {
    setFilterType("");
    setFilterEmail("");
    setPage(1);
    fetchData();
  };

  const openModal = (payload) => {
    setSelectedPayload(payload);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedPayload(null);
    setShowModal(false);
  };

  const getRecipients = (log) => {
    return (Array.isArray(log?.eventRecipients) && log?.eventRecipients?.length > 0 ? log?.eventRecipients : log?.recipients) || []
  };

  const totalPages = Math.ceil(totalLogs / logsPerPage);

  return (
    <Layout>
      <div>
        <Button variant="secondary" className="mb-3" onClick={handleBack}>
          ← Back
        </Button>

        <h3 className="mb-4">Campaign Run Report</h3>

        {(loadingStates.campaign || loadingStates.sns) ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            {camapignMoreInfo && typeof camapignMoreInfo === "object" && (
              <Card className="mb-4 shadow-sm">
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}><strong>Campaign:</strong> {camapignMoreInfo.campaignName || "NA"}</Col>
                    <Col md={6}><strong>Channel:</strong> {camapignMoreInfo.channelName || "NA"}</Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Trigger Time:</strong>{" "}
                      {(() => {
                        const rawDate = camapignMoreInfo?.scheduledTime;
                        if (!rawDate) return "NA";
                        let dt = DateTime.fromISO(rawDate);
                        if (!dt.isValid) {
                          dt = DateTime.fromFormat(rawDate, "yyyy-MM-dd H:m:s");
                        }
                        return dt.isValid ? dt.toFormat("yyyy-MM-dd HH:mm:ss") : "Invalid Date";
                      })()}
                    </Col>
                    <Col md={6}><strong>Status:</strong> {camapignMoreInfo.status ?? "NA"}</Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}><strong>Audience Size:</strong> {camapignMoreInfo.totalAudienceCount || camapignMoreInfo.totalAudienceSize || 'NA'}</Col>
                    <Col md={6}><strong>Total Emails Count (To + Cc + Bcc):</strong> {camapignMoreInfo.totalEmailsCount ?? "NA"}</Col>

                  </Row>
                  <Row className="mb-3">
                    <Col md={6}><strong>Total Valid Emails Count (To + Cc + Bcc):</strong> {camapignMoreInfo.totalValidEmails ?? "NA"}</Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            <DataIssueSummary campaignData={camapignMoreInfo} />

            {snsStatSummary && typeof snsStatSummary === "object" && (
              <Card className="mb-4 shadow-sm">
                <Card.Header><strong>SNS Event Summary</strong></Card.Header>
                <Card.Body className="p-0">
                  <Table striped bordered hover responsive className="mb-0 text-center">
                    <thead>
                      <tr style={{ backgroundColor: "#e0e0e0" }}>
                        {Object.keys(snsStatSummary).map((eventType) => (
                          <th key={eventType}>
                            {eventType.charAt(0).toUpperCase() + eventType.slice(1)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {Object.values(snsStatSummary).map((count, idx) => (
                          <td key={idx}>{count}</td>
                        ))}
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3 shadow-sm">
              <Card.Body>
                <Row className="align-items-end">
                  <Col md={4}>
                    <Form.Label>Delivery Type</Form.Label>
                    <Form.Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                      <option value="">All</option>
                      <option value="Send">Send</option>
                      <option value="Bounce">Bounce</option>
                      <option value="Delivery">Delivery</option>
                      <option value="DeliveryDelay">DeliveryDelay</option>
                      <option value="Reject">Reject</option>
                      <option value="Rendering Failure">RenderingFailure</option>
                      <option value="Complaint">Complaint</option>
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter recipient email"
                      value={filterEmail}
                      onChange={(e) => setFilterEmail(e.target.value)}
                    />
                  </Col>
                  <Col md={4} className="d-flex gap-2">
                    <Button variant="primary" onClick={handleFilter}>Filter</Button>
                    <Button variant="outline-secondary" onClick={handleReset}>Reset</Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {loadingStates.logs ? (
              <div className="text-center my-5">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : (
              <>
                {error && <Alert variant="danger" className="text-center">{error}</Alert>}

                {Array.isArray(venderLogsArr) && venderLogsArr.length > 0 && (
                  <>
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>S.No.</th>
                          <th>Delivery Type</th>
                          <th>Message ID</th>
                          <th>Date and Time</th>
                          <th>Recipients</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {venderLogsArr.map((log, index) => {
                          const recipients = getRecipients(log);
                          const isFailure = ["bounce", "reject", "renderingfailure", "complaint"].includes(
                            (log.event || "").toLowerCase()
                          );
                          return (
                            <tr key={log._id}>
                              <td>{(page - 1) * logsPerPage + index + 1}</td>
                              <td style={{ color: isFailure ? "red" : "green" }}>{log.event}</td>
                              <td>{log.messageId || "NA"}</td>
                              <td>{log.timestamp ? new Date(log.timestamp).toLocaleString() : "NA"}</td>
                              <td>{recipients.join(", ")}</td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => openModal(log)}
                                >
                                  <FileCode className="me-1" title="View JSON" /> JSON
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    {/* Debug Info - Moved to bottom */}
                    <div className="text-center mt-3 text-muted">
                      Showing {venderLogsArr.length} logs of {totalLogs} total. Page {page} of {totalPages}.
                    </div>

                    {totalPages > 1 && (
                      <SmartPagination totalPages={totalPages} currentPage={page} onPageChange={setPage} />
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      <JsonModal show={showModal} onClose={closeModal} payload={selectedPayload} />
    </Layout>
  );
};

export default CampaignRunReportByUuid;
